// © Microsoft Corporation. All rights reserved.

import { Provider, connect } from "react-redux";
import { Store, StoreEnhancer, compose, createStore } from "redux";
import { getPageTimings, logger } from "@msnews/diagnostics";

import { DynamicReducer } from "../reducers";
import { IDataConnector } from "../connectors/IDataConnector";
import { JSSManager } from "@microsoft/fast-jss-manager-react";
import React from "react";
import ReactDom from "react-dom";
import { applyMiddleware } from "redux-subspace";
import { canUseDOM } from "@msnews/isomorphic-util";
import { create } from "jss";
import { createGenerateClassName } from "./PageHelper";
import { getStateFromRenderCache } from "@msnews/render-cache";
import jssGlobal from "jss-global";
import jssNested from "jss-nested";

declare global {
    interface Window {
        /**
         * Enable redux devtools extension debugging
         */
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: any;

        /**
         * Boolean flag to indicate if render cache hydration is happening
         */
        needsHydrate?: boolean;
    }
}

/**
 * Page Base.
 * Each page or hybrid page/experience must have an instance of the PageBase, which contains the  the store, and dynamic reducer
 * for the experience/app.
 * PageBase is setup as a singleton, so the proper usage in consumers is:
 * var pageBase = PageBase.getInstance();
 * @export
 * @class
 */
class PageBase {
    /**
     * The singleton instance of this class
     * @type: {PageBase}
     */
    private static _instance: PageBase;

    /**
     * The Dynamic reducer for this app/experience
     * @type: {DynamicReducer}
     */
    private _rootReducer: DynamicReducer;

    /**
     * The Redux store containing the top level application state. All component and data connectors will manage namespaced subspaces within
     * this store
     * @type {Store}
     */
    private _store: Store<any>;

    /**
     * @constructor
     */
    private constructor() {
        /**
         * In debug build, add the option to turn on redux-devtools-extension debugging.
         * More info here https://github.com/zalmoxisus/redux-devtools-extension
         */
        let enhancer: StoreEnhancer<any>;
        const middlewares: any = [];
        if (canUseDOM() && logger.isDebug()) {
            const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true, traceLimit: 25 }) || compose;
            enhancer = composeEnhancers(applyMiddleware(...middlewares)
            );
        } else {
            enhancer = applyMiddleware(...middlewares);
        }

        // Create the root reducer
        this._rootReducer = new DynamicReducer();

        // Check to see if this app instance was bootstrapped with a render-cache
        const initialState = canUseDOM() && window.needsHydrate && getStateFromRenderCache() || {};

        // create the store with a basic root reducer
        this._store = createStore(this.rootReducer.reduce, initialState, enhancer);
    }

    /**
     * Gets the Redux store for this app/experience
     * @method
     * @returns {Store<any>} The overall state for the App, which contains sub-states for each connector
     */
    public get store(): Store<any> {
        return this._store;
    }

    /**
     * Gets the root reducer for this app/experience
     * @returns {DynamicReducer} The reducer for the app/experience
     */
    public get rootReducer(): DynamicReducer {
        return this._rootReducer;
    }

    /**
     * Gets the singleton instance of this class for the app/experience
     * @returns {PageBase} The instance of PageBase for the page/hybrid experience
     */
    public static getInstance(): PageBase {
        return this._instance || (this._instance = new this());
    }

    /**
     * Starts the application by calling the onApplicationStart method for each connector that has been added to the app.
     * @function
     * @method
     */
    public start(): void {
        // Iterate over all of the connector's and call their OnApplicationStart method
        const allConnectors: IDataConnector<any, any>[] = this.rootReducer.connectors();
        allConnectors.forEach(connector => connector.onApplicationStart());
    }

    /**
     * Creates and renders a root element, that is connected to the store.
     * The root element will created with the passed in connected element as a child.
     * @function
     * @param {React.ReactElement<any>} connectedTopLevelElement The top level rendering element for the page/application.
     * @param {HTMLElement} hostElement The host element (usuall a <div>) to contain the msnews-experiences-redux application
     * @method
     */
    public renderRootElement(connectedTopLevelElement: React.ReactElement<any>, hostElement: HTMLElement): void {
        // Update the base JSS manager instance
        this.updateJssManagerInstance();

        // Create a ReactElement, that contains the Redux provider with the store, passing the incomming top level element in as a child, so that
        // the pageElement wraps the top level element with the redux store
        const rootElement = React.createElement(Provider, { store: this._store }, connectedTopLevelElement);

        // Capture Render cache state in page timings
        let pageTimings = getPageTimings();

        if (window.needsHydrate) {
            ReactDom.hydrate(rootElement, hostElement);
            pageTimings["isRenderCachePresent"]  = 1;
            window.needsHydrate = false;
        } else {
            pageTimings["isRenderCachePresent"]  = 0;
            ReactDom.render(rootElement, hostElement);
        }
    }

    /**
     * Updates the base JSSManager instance with the correct default plugins
     */
    private updateJssManagerInstance(): void {
        const jssInstance: ReturnType<create> = create({
            plugins: [jssNested(), jssGlobal()]
        });

        JSSManager.createGenerateClassName = createGenerateClassName;
        JSSManager.jss = jssInstance;
    }
}

export default PageBase;